<template>
  <div class="notification-center">
    <router-link
      v-if="enableOnlineOrders === 'true'"
      :to="{ name: 'OnlineOrder' }"
      class="header-action-btn header-action-btn--desktop-only text-white font-weight-bold text-decoration-none"
      title="Online Orders"
    >
      <svg class="icon icon-icon-dashboard-10">
        <use xlink:href="#icon-icon-dashboard-10" />
      </svg>
      <span class="header-action-btn__text">Online Orders</span>
    </router-link>

    <!-- Dismiss Alert Button - Outside dropdown -->
    <button
      v-if="isAlertActive"
      class="btn btn-sm btn-danger dismiss-alert-btn-external"
      @click="dismissAlert"
      title="Dismiss notification alert sound"
    >
      <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <path d="M11 5L6 9H2v6h4l5 4V5z" />
        <line
          x1="23"
          y1="9"
          x2="17"
          y2="15"
        />
        <line
          x1="17"
          y1="9"
          x2="23"
          y2="15"
        />
      </svg>
      <span class="dismiss-text">Dismiss Alert</span>
    </button>

    <!-- Notification Bell Button -->
    <button
      class="notification-bell-btn"
      @click="drawerOpen = true"
    >
      <div class="notification-bell-container">
        <div class="notification-bell-icon">
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
            <path d="M13.73 21a2 2 0 0 1-3.46 0" />
          </svg>
        </div>
        <span
          v-if="unreadCount > 0"
          class="notification-badge"
        >
          {{ unreadCount }}
        </span>
      </div>
    </button>

    <!-- Drawer Overlay -->
    <transition name="drawer-overlay">
      <div
        v-if="drawerOpen"
        class="drawer-overlay"
        @click="drawerOpen = false"
      />
    </transition>

    <!-- Drawer Panel -->
    <transition name="drawer-slide">
      <div
        v-if="drawerOpen"
        class="notification-drawer"
      >
        <div class="drawer-header">
          <h6 class="mb-0">
            Notifications
          </h6>
          <button
            class="drawer-close-btn"
            @click="drawerOpen = false"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="currentColor"
            >
              <path d="M19 6.41L17.59 5L12 10.59L6.41 5L5 6.41L10.59 12L5 17.59L6.41 19L12 13.41L17.59 19L19 17.59L13.41 12L19 6.41Z" />
            </svg>
          </button>
        </div>

        <div
          v-if="notifications.length > 0"
          class="drawer-actions"
        >
          <button
            class="btn btn-sm btn-outline-danger"
            @click="clearAllNotifications"
          >
            Clear All
          </button>
          <button
            class="btn btn-sm btn-outline-primary"
            @click="markAllAsRead"
          >
            Mark All Read
          </button>
        </div>

        <div class="notification-list">
          <div
            v-if="notifications.length === 0"
            class="notification-empty"
          >
            <p class="mb-0 text-muted">
              No notifications
            </p>
          </div>

          <div
            v-for="notification in notifications"
            :key="notification.id"
            class="notification-item"
            :class="{ unread: !notification.read }"
            @click="handleNotificationClick(notification)"
          >
            <div
              v-if="!notification.read"
              class="notification-dot"
            />

            <div class="notification-content">
              <div class="notification-title">
                {{ notification.title }}
              </div>
              <div class="notification-message">
                {{ notification.message }}
              </div>
              <div class="notification-time">
                {{ formatTime(notification.timestamp) }}
              </div>
            </div>

            <button
              class="notification-remove-btn"
              @click.stop="removeNotification(notification.id)"
              title="Remove notification"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path d="M19 6.41L17.59 5L12 10.59L6.41 5L5 6.41L10.59 12L5 17.59L6.41 19L12 13.41L17.59 19L19 17.59L13.41 12L19 6.41Z" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'

/**
 * NotificationCenter Component
 *
 * Displays a notification bell in the header with a drawer showing all notifications.
 * Handles notifications for:
 * - Curbside pickup orders
 * - New online orders
 * - Application updates
 *
 * Notifications are managed through Vuex store and automatically added by pusherMixin
 * when events are received from the server. Click behavior is determined by the notification data.
 */
export default {
  name: 'NotificationCenter',
  data() {
    return {
      drawerOpen: false
    }
  },
  computed: {
    ...mapGetters({
      notifications: 'notifications/allNotifications',
      unreadCount: 'notifications/unreadCount',
      isAlertActive: 'notifications/isAlertActive',
      enableOnlineOrders: 'config/enableOnlineOrders'
    })
  },
  methods: {
    ...mapActions({
      markAsRead: 'notifications/markAsRead',
      markAllAsRead: 'notifications/markAllAsRead',
      removeNotification: 'notifications/removeNotification',
      clearAllNotifications: 'notifications/clearAllNotifications',
      dismissAlert: 'notifications/dismissAlert'
    }),

    handleNotificationClick(notification) {
      // Mark as read
      this.markAsRead(notification.id);

      // Handle notification click based on data
      if (notification.data) {
        // If it's a curbside pickup order
        if (notification.data.order_id || notification.data.id) {
          this.$store.dispatch(
            "order/setCurbsidePickupOrder",
            notification.data,
          );
          this.$bvModal.show("curbside-pickup");
        }
        // Online order notifications should land on the online orders queue.
        else if (
          notification.data.call === "orderUpdate" ||
          notification.data.call === "fetchPendingOrder" ||
          notification.data.call === "newOnlineOrder"
        ) {
          this.pushRoute('/online-orders');
        }
        // If it's a new version
        else {
          this.$bvModal.show('sw-update-modal')
        }
      }

      this.drawerOpen = false
    },

    pushRoute(route) {
      this.$router.push(route).catch(err => {
        if (err.name !== 'NavigationDuplicated') {
          console.error('Navigation error:', err);
        }
      });
    },

    formatTime(timestamp) {
      const now = new Date()
      const notificationTime = new Date(timestamp)
      const diffInMinutes = Math.floor((now - notificationTime) / (1000 * 60))

      if (diffInMinutes < 1) {
        return 'Just now'
      } else if (diffInMinutes < 60) {
        return `${diffInMinutes}m ago`
      } else if (diffInMinutes < 1440) {
        const hours = Math.floor(diffInMinutes / 60)
        return `${hours}h ago`
      } else {
        return notificationTime.toLocaleDateString()
      }
    }
  }
}
</script>

<style scoped lang="scss">
.notification-center {
  position: relative;
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.notification-bell-btn {
  border: none;
  background: transparent;
  padding: 0;
  cursor: pointer;
}

.notification-bell-container {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 36px;
  min-width: 36px;
  height: 36px;
  min-height: 36px;
  max-height: 36px;
  padding: 0;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 6px;
  transition: background 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }
}

.notification-bell-icon {
  display: flex;
  align-items: center;

  svg {
    width: 18px !important;
    height: 18px !important;
    color: #fff;
    min-width: 18px;
  }
}

.notification-badge {
  position: absolute;
  top: 2px;
  right: 2px;
  background: #dc3545;
  color: white;
  border-radius: 50%;
  width: 18px;
  height: 18px;
  font-size: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  animation: pulse 2s infinite;
}

// Drawer overlay
.drawer-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.4);
  z-index: 1049;
}

.drawer-overlay-enter-active,
.drawer-overlay-leave-active {
  transition: opacity 0.3s ease;
}

.drawer-overlay-enter,
.drawer-overlay-leave-to {
  opacity: 0;
}

// Drawer panel
.notification-drawer {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  width: 380px;
  max-width: 90vw;
  background: #fff;
  z-index: 1050;
  display: flex;
  flex-direction: column;
  box-shadow: -4px 0 20px rgba(0, 0, 0, 0.15);
}

.drawer-slide-enter-active,
.drawer-slide-leave-active {
  transition: transform 0.3s ease;
}

.drawer-slide-enter,
.drawer-slide-leave-to {
  transform: translateX(100%);
}

.drawer-header {
  padding: 16px 20px;
  border-bottom: 1px solid #e9ecef;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #f8f9fa;
  flex-shrink: 0;

  h6 {
    font-weight: 600;
    font-size: 16px;
    color: #495057;
  }
}

.drawer-close-btn {
  border: none;
  background: transparent;
  color: #6c757d;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover {
    background: #e9ecef;
    color: #212529;
  }
}

.drawer-actions {
  padding: 10px 20px;
  border-bottom: 1px solid #e9ecef;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-shrink: 0;
  gap: 10px;
}

.dismiss-alert-btn-external {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  gap: 6px;
  height: 36px;
  min-height: 36px;
  max-height: 36px;
  padding: 0 12px;
  font-size: 13px;
  font-weight: 600;
  border-radius: 6px;
  background: #dc3545;
  border: none;
  color: white;
  animation: pulse-red 2s infinite;
  transition: background 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease;
  white-space: nowrap;

  &:hover {
    background: #c82333;
    transform: scale(1.05);
    box-shadow: 0 4px 8px rgba(220, 53, 69, 0.3);
  }

  &:active {
    transform: scale(0.98);
  }

  svg {
    fill: none;
    flex-shrink: 0;
  }

  .dismiss-text {
    display: none;

    @media (min-width: 1312px) {
      display: inline-block;
    }
  }

  @media (min-width: 768px) and (max-width: 1311.98px) {
    width: 36px;
    min-width: 36px;
    padding: 0;
  }
}

@keyframes pulse-red {
  0% {
    box-shadow: 0 0 0 0 rgba(220, 53, 69, 0.7);
  }
  50% {
    box-shadow: 0 0 0 8px rgba(220, 53, 69, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(220, 53, 69, 0);
  }
}

.notification-list {
  flex: 1;
  overflow-y: auto;
}

.notification-empty {
  padding: 40px 20px;
  text-align: left;
}

.notification-item {
  display: flex;
  align-items: center;
  padding: 14px 20px;
  border-bottom: 1px solid #f1f3f4;
  cursor: pointer;
  transition: background-color 0.2s ease;
  gap: 12px;
  text-align: left;

  &:hover {
    background-color: #f8f9fa;
  }

  &.unread {
    background-color: #e3f2fd;

    &:hover {
      background-color: #bbdefb;
    }
  }

  &:last-child {
    border-bottom: none;
  }
}

.notification-dot {
  width: 8px;
  height: 8px;
  min-width: 8px;
  background: #007bff;
  border-radius: 50%;
  flex-shrink: 0;
}

.notification-content {
  flex: 1;
  min-width: 0;
  text-align: left;
}

.notification-title {
  font-weight: 600;
  color: #212529;
  margin-bottom: 2px;
  font-size: 14px;
}

.notification-message {
  color: #6c757d;
  font-size: 13px;
  margin-bottom: 4px;
  line-height: 1.4;
}

.notification-time {
  color: #adb5bd;
  font-size: 11px;
}

.notification-remove-btn {
  width: 36px;
  height: 36px;
  min-width: 36px;
  border: 1px solid #dee2e6;
  background: #fff;
  color: #6c757d;
  cursor: pointer;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: all 0.2s ease;

  &:hover {
    background: #fee2e2;
    border-color: #dc3545;
    color: #dc3545;
  }

  &:active {
    background: #fecaca;
  }

  svg {
    width: 16px;
    height: 16px;
  }
}

@keyframes pulse {
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.1);
  }
  100% {
    transform: scale(1);
  }
}
</style>
